import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { NoteModel } from 'src/app/models/invoicing/note.model';
import { RequestNotePrintModel } from 'src/app/models/invoicing/request-note-print.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { RequestNoteModalModel } from 'src/app/models/tables/invoicing/request-note-modal.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RequestNoteService {

  private readonly _httpClient = inject(HttpClient);

  annulInvoiceByRequestNote(noteId: number): Observable<NoteModel> {
    return this._httpClient.put<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.AnnulInvoiceByRequestNoteAsync}/${noteId}`,
      null);
  }

  annulRequestNote(noteId: number): Observable<NoteModel> {
    return this._httpClient.put<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.AnnulRequestNoteAsync}/${noteId}`,
      null);
  }

  attachFilesToRequestNote(noteId: number, files: FormData): Observable<Array<string>> {
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.AttachFilesToRequestNote}/${noteId}`, files);
  }

  createRequestNote(requestNoteContract: NoteModel): Observable<NoteModel> {
    return this._httpClient.post<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.CreateNoteAsync}`, requestNoteContract);
  }

  deleteRequestNote(noteId: number): Observable<NoteModel> {
    return this._httpClient.delete<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.DeleteRequestNoteAsync}/${noteId}`);
  }

  deleteFilesToRequestNote(noteId: number, file: string): Observable<Array<string>> {
    const itemFilterResponse = new ItemFilterModel("file", null, noteId, file);
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.DeleteFilesToRequestNote}`, itemFilterResponse);
  }

  downloadFileToRequestNote(noteId: number, file: string): Observable<HttpEvent<Blob>> {
    const itemFilterResponse = new ItemFilterModel("file", null, noteId, file);
    return this._httpClient.request(new HttpRequest('POST', `${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.DownloadFileToRequestNote}`,
      itemFilterResponse, { reportProgress: true, responseType: 'blob' }));
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetColumnsForGeneralConsultationAsync}`);
  }

  getColumnsForRequestNotesAsync(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetColumnsForRequestNotesAsync}`);
  }

  getEmptyRequestNotePrint(): Observable<RequestNotePrintModel> {
    return this._httpClient.get<RequestNotePrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetEmptyRequestNotePrint}`);
  }

  getFilesToRequestNote(noteId: number): Observable<any[]> {
    return this._httpClient.get<any[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetFilesToRequestNote}/${noteId}`,
    );
  }

  getFirstRequestNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number): Observable<NoteModel> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('enterpriseId', enterpriseId)
      .set('bussinessDocumentsTypeId', bussinessDocumentsTypeId)
      .set('noteTypeId', noteTypeId);
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetFirstRequestNoteByKeysAsync}`, { params });
  }

  getLastRequestNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number): Observable<NoteModel> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('enterpriseId', enterpriseId)
      .set('bussinessDocumentsTypeId', bussinessDocumentsTypeId)
      .set('noteTypeId', noteTypeId);
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetLastRequestNoteByKeysAsync}`, { params });
  }

  getNextRequestNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number,
    consecutive: number | null): Observable<NoteModel> {
    let params = new HttpParams()
      .set('branchId', branchId)
      .set('enterpriseId', enterpriseId)
      .set('bussinessDocumentsTypeId', bussinessDocumentsTypeId)
      .set('noteTypeId', noteTypeId);
    if (consecutive !== null) params = params.set('requestConsecutive', consecutive.toString());
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetNextRequestNoteByKeysAsync}`, { params });
  }

  getPermissionPrint(): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetPermissionPrintAsync}`);
  }

  getPreviusRequestNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number,
    consecutive: number): Observable<NoteModel> {
    let params = new HttpParams()
      .set('branchId', branchId)
      .set('enterpriseId', enterpriseId)
      .set('bussinessDocumentsTypeId', bussinessDocumentsTypeId)
      .set('noteTypeId', noteTypeId);
    if (consecutive !== null) params = params.set('requestConsecutive', consecutive.toString());
    return this._httpClient.get<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetPreviusRequestNoteByKeysAsync}`, { params });
  }

  getRequestNoteByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, noteTypeId: number, consecutive: number):
    Observable<any> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('enterpriseId', enterpriseId);
    params = params.set('bussinessDocumentsTypeId', bussinessDocumentsTypeId);
    params = params.set('noteTypeId', noteTypeId);
    params = params.set('consecutive', consecutive.toString());
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetRequestNoteByKeysAsync}`, { params: params });
  }

  getRequestNoteByKeysByPrintAsync(branchId: number, consecutive: number, print: boolean, noteId: number | null): Observable<RequestNotePrintModel> {
    let params = new HttpParams();
    params = params.set('branchId', branchId);
    params = params.set('consecutive', consecutive);
    params = params.set('print', print.toString());
    if (noteId !== null) params = params.set('noteId', noteId);
    return this._httpClient.get<RequestNotePrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetRequestNoteByKeysByPrintAsync}`,
      { params }
    );
  }

  getRequestNotesForNoteAsync(branchId: number, enterpriseId: number, noteTypeId: number, bussinessDocumentsTypeId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<RequestNoteModalModel>> {
    const params = new HttpParams().set('branchId', branchId.toString()).set('enterpriseId', enterpriseId.toString()).set('noteTypeId', noteTypeId.toString()).set('bussinessDocumentsTypeId', bussinessDocumentsTypeId.toString()).set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<RequestNoteModalModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetRequestNotesForNoteAsync}`,
      filters, { params: params }
    );
  }

  getRequestNotesWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.GetAllRequestNotesWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  setRequestNoteStateAsync(noteId: number, open: boolean): Observable<NoteModel> {
    return this._httpClient.put<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.SetRequestNoteStateAsync}/${noteId}/${open}`,
      null);
  }

  updateRequestNote(requestNoteContract: NoteModel): Observable<NoteModel> {
    return this._httpClient.put<NoteModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.requestNote.UpdateRequestNoteasync}`,
      requestNoteContract);
  }
}